<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-container v-if="!loadingPersonal">
          <v-row align="center" class="mb-4" v-if="personalCompleto">
            <v-col cols="12" md="2" class="d-flex justify-center">
              <v-avatar color="secondary" rounded size="80" v-if="!personalCompleto.foto">
                <v-icon size="64" color="white">mdi-account</v-icon>
              </v-avatar>
              <v-img
                v-else
                :src="personalCompleto.foto"
                max-height="80"
                max-width="80"
                class="rounded"
                @error="personalCompleto.foto = null"
              ></v-img>
            </v-col>
            <v-col cols="12" md="10">
              <div class="text-h6 font-weight-bold text--primary">
                {{ getFullName(personalCompleto) }}
              </div>
              <div class="text-subtitle-2 text--secondary mt-1">CUIP: {{ personalCompleto.CUIP || 'Sin CUIP' }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="text-h6 font-weight-bold mb-3">Historial de Asistencias</div>
              <v-card class="card" color="primary" :loading="loading">
                <v-card-text>
                  <v-data-table
                    :headers="headers"
                    :items="asistencias"
                    class="transparent rounded-lg"
                    :loading="loading"
                    loading-text="Cargando asistencias..."
                    :items-per-page="10"
                    :footer-props="{
                      'items-per-page-options': [5, 10, 15, 20, -1],
                      'items-per-page-text': 'Registros por página:',
                      'show-current-page': true,
                      'show-first-last-page': true,
                    }"
                  >
                    <template v-slot:[`item.fecha`]="{ item }">
                      {{ formatDate(item.fecha) }}
                    </template>
                    <template v-slot:[`item.tipoAsistencia`]="{ item }">
                      <v-chip small :color="getTipoAsistenciaColor(item.fk_idTipoAsistencia)" text-color="white">
                        {{ getTipoAsistenciaTitle(item.fk_idTipoAsistencia) }}
                      </v-chip>
                    </template>
                  </v-data-table>
                  <div v-if="!loading && (!asistencias || asistencias.length === 0)" class="text-center py-8">
                    <v-icon large color="secondary">mdi-clipboard-text-off-outline</v-icon>
                    <div class="text-subtitle-1 text--primary mt-2">
                      El personal seleccionado no tiene asistencias registradas.
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
        <!-- Loader mientras carga el personal -->
        <v-container v-else>
          <v-row align="center" justify="center" class="py-12">
            <v-col cols="12" align="center">
              <v-progress-circular indeterminate color="secondary" size="64"></v-progress-circular>
              <div class="text-subtitle-1 text--primary mt-4">Cargando información del personal...</div>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'ListadoAsistenciasModal',

  props: {
    selectedPersonal: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      headers: [
        { text: 'Fecha', value: 'fecha', width: '200px', align: 'center' },
        { text: 'Tipo de Asistencia', value: 'tipoAsistencia', width: '250px' },
      ],
      loading: false,
      loadingPersonal: false,
      personalCompleto: null,
      DISPLAY_DATE_FORMAT: 'DD/MM/YYYY',
    }
  },

  computed: {
    ...mapGetters('asistencia', {
      asistencias: 'getAsistencias',
      tiposAsistencia: 'getTiposAsistencia',
    }),
  },

  methods: {
    ...mapActions('asistencia', ['fetchAsistencias', 'fetchTipoAsistencia']),
    ...mapActions('personal', ['fetchPersonalById']),
    ...mapMutations(['SET_SNACKBAR_MESSAGE']),

    formatDate(date) {
      if (!date) return 'Sin fecha'

      let cleanDate = date

      if (typeof date === 'string' && date.includes(' ')) {
        cleanDate = date.split(' ')[0]
      }
      const momentDate = moment(cleanDate, 'DD/MM/YYYY', true)

      if (!momentDate.isValid()) {
        console.warn('Fecha inválida recibida:', date)
        return 'Fecha inválida'
      }

      return momentDate.format(this.DISPLAY_DATE_FORMAT)
    },

    getTipoAsistenciaTitle(idTipoAsistencia) {
      if (!this.tiposAsistencia || this.tiposAsistencia.length === 0) {
        return 'Cargando...'
      }

      const tipo = this.tiposAsistencia.find((t) => t.id === idTipoAsistencia)
      return tipo ? tipo.title : 'Desconocido'
    },

    getTipoAsistenciaColor(idTipoAsistencia) {
      const colorMap = {
        1: 'success', // Asistencia
        2: 'warning', // Retardo
        3: 'error', // Falta
        4: 'info', // Justificada
      }
      return colorMap[idTipoAsistencia] || 'secondary'
    },

    getFullName(person) {
      if (!person) return 'Sin nombre'

      const nombre = person.nombre || ''
      const segundoNombre = person.segundoNombre || ''
      const apaterno = person.apaterno || ''
      const amaterno = person.amaterno || ''

      return `${apaterno} ${amaterno} ${nombre} ${segundoNombre}`.trim()
    },

    async fetchPersonalCompleto() {
      if (!this.selectedPersonal) return

      try {
        this.loadingPersonal = true
        const idPersonal = this.selectedPersonal.idPersonal || this.selectedPersonal.id_personal

        await this.fetchPersonalById(idPersonal)

        this.personalCompleto = this.$store.getters['personal/getPersonalSingle']
      } catch (error) {
        this.SET_SNACKBAR_MESSAGE('Error al cargar la información del personal.')
        this.personalCompleto = null
      } finally {
        this.loadingPersonal = false
      }
    },

    async loadData() {
      try {
        this.loading = true

        await Promise.all([this.fetchTipoAsistencia(), this.fetchPersonalCompleto()])

        const personalId = this.selectedPersonal.idPersonal || this.selectedPersonal.id_personal
        await this.fetchAsistencias(personalId)
      } catch (error) {
        this.SET_SNACKBAR_MESSAGE('Error al cargar las asistencias.')
      } finally {
        this.loading = false
      }
    },
  },

  async mounted() {
    moment.locale('es')
    await this.loadData()
  },
}
</script>

<style scoped>
.rounded {
  border-radius: 8px;
}
</style>
